/**
 * Pool of recurring clients (option 6a — fixed roster of 15).
 * Each player game starts with the same roster but their loyalty scores
 * evolve independently per save.
 *
 * Segments :
 *   - GC : Grand Compte (premium, prix élevés, exigent)
 *   - PME : Petite/Moyenne Entreprise (équilibré, opportuniste)
 *   - PUB : Public/Collectivité (volume stable, délais de paiement longs)
 *
 * `themes` recense les types de contrats que ce client préfère — sera utilisé
 * pour le scoring de fidélité quand un contrat compatible est proposé.
 */

export const RECURRENT_CLIENTS = [
  { id: 'cli-translog',     name: 'TransLog SA',           segment: 'GC',  city: 'Lyon',       themes: ['urbain', 'régional'],   color: '#2563EB', avatar: 'TL', logoUrl: '/client-logos/cli-translog.webp' },
  { id: 'cli-marketcorp',   name: 'MarketCorp',            segment: 'GC',  city: 'Paris',      themes: ['urbain', 'national'],   color: '#7C3AED', avatar: 'MC', logoUrl: '/client-logos/cli-marketcorp.webp' },
  { id: 'cli-logifrance',   name: 'LogiFrance',            segment: 'GC',  city: 'Marseille',  themes: ['national', 'frigorifique'], color: '#DB2777', avatar: 'LF', logoUrl: '/client-logos/cli-logifrance.webp' },
  { id: 'cli-fraisexpress', name: 'Frais Express',         segment: 'PME', city: 'Rennes',     themes: ['frigorifique', 'régional'], color: '#06B6D4', avatar: 'FE', logoUrl: '/client-logos/cli-fraisexpress.webp' },
  { id: 'cli-batipro',      name: 'BatiPro Distribution',  segment: 'PME', city: 'Bordeaux',   themes: ['régional', 'lourd'],    color: '#D97706', avatar: 'BP', logoUrl: '/client-logos/cli-batipro.webp' },
  { id: 'cli-mariescuisine',name: 'Marie\'s Cuisine',      segment: 'PME', city: 'Lille',      themes: ['urbain', 'frigorifique'], color: '#10B981', avatar: 'MC', logoUrl: '/client-logos/cli-mariescuisine.webp' },
  { id: 'cli-electromax',   name: 'Électromax',            segment: 'GC',  city: 'Toulouse',   themes: ['national', 'urbain'],   color: '#F59E0B', avatar: 'EX', logoUrl: '/client-logos/cli-electromax.webp' },
  { id: 'cli-vinsdufrance', name: 'Vins du France',        segment: 'PME', city: 'Reims',      themes: ['régional', 'international'], color: '#9333EA', avatar: 'VF', logoUrl: '/client-logos/cli-vinsdufrance.webp' },
  { id: 'cli-pharmaceutic', name: 'Pharmaceutic Direct',   segment: 'GC',  city: 'Strasbourg', themes: ['urbain', 'frigorifique'], color: '#0EA5E9', avatar: 'PD', logoUrl: '/client-logos/cli-pharmaceutic.webp' },
  { id: 'cli-textileco',    name: 'TextileCo',             segment: 'PME', city: 'Lyon',       themes: ['national', 'régional'], color: '#EC4899', avatar: 'TX', logoUrl: '/client-logos/cli-textileco.webp' },
  { id: 'cli-mairielyon',   name: 'Mairie de Lyon',        segment: 'PUB', city: 'Lyon',       themes: ['urbain'],                color: '#475569', avatar: 'ML', logoUrl: '/client-logos/cli-mairielyon.webp' },
  { id: 'cli-cci-paris',    name: 'CCI Paris',             segment: 'PUB', city: 'Paris',      themes: ['urbain', 'régional'],   color: '#374151', avatar: 'CP', logoUrl: '/client-logos/cli-cci-paris.webp' },
  { id: 'cli-conseilreg',   name: 'Conseil Régional',      segment: 'PUB', city: 'Marseille',  themes: ['régional', 'national'], color: '#1F2937', avatar: 'CR', logoUrl: '/client-logos/cli-conseilreg.webp' },
  { id: 'cli-meublesplus',  name: 'Meubles Plus',          segment: 'PME', city: 'Nantes',     themes: ['lourd', 'régional'],    color: '#84CC16', avatar: 'M+', logoUrl: '/client-logos/cli-meublesplus.svg' },
  { id: 'cli-eurolog',      name: 'EuroLog International', segment: 'GC',  city: 'Lille',      themes: ['international', 'national'], color: '#0F766E', avatar: 'EL', logoUrl: '/client-logos/cli-eurolog.webp' },
];

/** Map id → client for fast lookup. */
export const CLIENT_BY_ID = RECURRENT_CLIENTS.reduce((acc, c) => {
  acc[c.id] = c; return acc;
}, {});

/**
 * 5 loyalty tiers (option 2 — ladder validated by user).
 * Each tier surfaces in the UI as a coloured badge and triggers different
 * gameplay effects (handled in applyRelationshipEffects).
 */
export const LOYALTY_TIERS = [
  { id: 'mefiant',    label: 'Méfiant',             min: 0,  max: 20, color: '#DC2626', icon: 'ShieldAlert' },
  { id: 'neutre',     label: 'Neutre',              min: 20, max: 40, color: '#9CA3AF', icon: 'Minus' },
  { id: 'confiant',   label: 'Confiant',            min: 40, max: 65, color: '#0891B2', icon: 'Handshake' },
  { id: 'fidele',     label: 'Fidèle',              min: 65, max: 85, color: '#D97706', icon: 'Star' },
  { id: 'partenaire', label: 'Partenaire stratégique', min: 85, max: 101, color: '#10B981', icon: 'Crown' },
];

/** Returns the tier object for a given loyalty score (0..100). */
export function getLoyaltyTier(score) {
  const s = Math.max(0, Math.min(100, score || 0));
  return LOYALTY_TIERS.find(t => s >= t.min && s < t.max) || LOYALTY_TIERS[1];
}

/**
 * Events that mutate the loyalty score (option 3 — validated by user).
 * Returned as a constant catalogue so the UI can render the history nicely.
 */
export const RELATIONSHIP_EVENTS = {
  delivery_completed_ontime:   { delta: +1,  label: 'Livraison hebdo à l\'heure' },
  contract_completed_ontime:   { delta: +2,  label: 'Contrat terminé à l\'heure' },
  contract_completed_late:     { delta: -4,  label: 'Livraison en retard' },
  contract_refused:            { delta: -3,  label: 'Refus d\'un contrat proposé' },
  contract_cancelled:          { delta: -15, label: 'Annulation de contrat' },
  renegotiation_accepted:      { delta: +5,  label: 'Renégociation acceptée' },
  renegotiation_refused:       { delta: -2,  label: 'Renégociation refusée' },
  yearly_no_incident:          { delta: +10, label: '1 an sans incident' },
  reputation_drift_positive:   { delta: +0.5, label: 'Bonne réputation publique' },
  // 28/07/2026 — Actions commerciales proactives (page Clients).
  commercial_visit:            { delta: +6,  label: 'Visite commerciale' },
  event_invitation:            { delta: +8,  label: 'Invitation à un événement' },
  mission_request_success:     { delta: +2,  label: 'Mission sollicitée obtenue' },
  winback_plan:                { delta: +13, label: 'Plan de reconquête' },
  // 18/09/2026 — Impact client renforcé (REX apprenant) : pénalités de
  // service + bouche-à-oreille négatif vers les autres clients du segment.
  service_penalty:             { delta: -6,  label: 'Pénalité de service (livraisons manquées)' },
  word_of_mouth_negative:      { delta: -2,  label: 'Bouche-à-oreille négatif (autre client)' },
};

/**
 * Bootstrap loyalty data — called from createCompany so every client
 * starts in a neutral state (score 30, no history).
 */
export function buildInitialClientLoyalty() {
  const out = {};
  RECURRENT_CLIENTS.forEach(c => {
    out[c.id] = {
      score: 30, // Start at 'Neutre'
      events: [],
      coolingUntilDay: 0, // 'Méfiant' freeze window
      yearlyIncidentResetDay: 0,
      yearlyHadIncident: false,
    };
  });
  return out;
}

/**
 * Apply an event to a client's loyalty data. Returns a NEW relationship
 * dict (does not mutate). Use in slice via spread.
 *
 * @param {Object} loyalty       — state.clientLoyalty
 * @param {string} clientId
 * @param {string} eventId       — key from RELATIONSHIP_EVENTS
 * @param {number} day           — current totalDays for event logging
 * @param {Object} extra         — optional { contractId, value, note }
 */
export function applyRelationshipEvent(loyalty, clientId, eventId, day, extra = {}) {
  const event = RELATIONSHIP_EVENTS[eventId];
  if (!event) return loyalty;
  const current = loyalty[clientId] || { score: 30, events: [], coolingUntilDay: 0, yearlyHadIncident: false };
  const newScore = Math.max(0, Math.min(100, current.score + event.delta));
  const newEvents = [
    { eventId, label: event.label, delta: event.delta, day, ...extra },
    ...(current.events || []),
  ].slice(0, 30); // keep last 30 events per client
  // If we just dropped into 'Méfiant' AND no cooldown has ever been set
  // before, set a 30-day cooldown during which this client won't propose
  // new contracts (option 4c). Once a client has already gone through a
  // Méfiant phase (cooldown != 0), subsequent re-entries don't reset it —
  // they live with the memory of the previous incident.
  const isInMefiant = newScore < 20;
  const cooldownEverSet = (current.coolingUntilDay || 0) > 0;
  const coolingUntilDay = (isInMefiant && !cooldownEverSet)
    ? day + 30
    : current.coolingUntilDay;
  // Mark yearly incident if applicable.
  const yearlyHadIncident = event.delta < 0
    ? true
    : current.yearlyHadIncident;
  return {
    ...loyalty,
    [clientId]: {
      ...current,
      score: newScore,
      events: newEvents,
      coolingUntilDay,
      yearlyHadIncident,
    },
  };
}

/**
 * Discount applied to a contract price for a Fidèle/Partenaire client.
 * Returns the multiplier (1.00 = no discount).
 */
export function getClientPriceMultiplier(loyalty, clientId) {
  const data = loyalty?.[clientId];
  if (!data) return 1.0;
  const tier = getLoyaltyTier(data.score);
  // option 4a — -2% discount for Fidèle / Partenaire clients.
  if (tier.id === 'fidele' || tier.id === 'partenaire') return 0.98;
  return 1.0;
}

/**
 * Returns true if this client is currently in the 30-day "Méfiant" freeze
 * and shouldn't propose new contracts.
 */
export function isClientOnCooldown(loyalty, clientId, day) {
  const data = loyalty?.[clientId];
  if (!data) return false;
  return (day || 0) < (data.coolingUntilDay || 0);
}
